import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";
// Every page (and every dialog it opens) renders inside the kit scope: brand colour schemes + the
// 44px / 14px readability floor applied once (POLISH v1, 2026-10-15).
import { withSopKit } from "./components/ui/SopKitScope";

// Admin (permission-gated)
const SopAdminList = React.lazy(() => import("./SopAdminList").then((m) => ({ default: withSopKit(m.default) })));
const SopEditor = React.lazy(() => import("./SopEditor").then((m) => ({ default: withSopKit(m.default) })));
const SopWizard = React.lazy(() => import("./SopWizard").then((m) => ({ default: withSopKit(m.default) })));
const SopPresenter = React.lazy(() => import("./SopPresenter").then((m) => ({ default: withSopKit(m.default) })));
const SopSettingsPage = React.lazy(() => import("./SopSettings").then((m) => ({ default: withSopKit(m.default) })));
const SopTopics = React.lazy(() => import("./SopTopics").then((m) => ({ default: withSopKit(m.default) })));
const SopComments = React.lazy(() => import("./SopComments").then((m) => ({ default: withSopKit(m.default) })));
const SopAgenda = React.lazy(() => import("./SopAgenda").then((m) => ({ default: withSopKit(m.default) })));

// Public (no auth)
const SopCatalogue = React.lazy(() => import("./SopCatalogue").then((m) => ({ default: withSopKit(m.default) })));
const SopEpisodePage = React.lazy(() => import("./SopEpisodePage").then((m) => ({ default: withSopKit(m.default) })));
const SopTopicPage = React.lazy(() => import("./SopTopicPage").then((m) => ({ default: withSopKit(m.default) })));

const stateOfPlayModuleConfig: ModuleConfig = {
  id: "state-of-play",
  name: "State of Play",
  description:
    "Short recorded audio events with a public replay page — schedule from the calendar, present one diagram, and the transcript becomes the page",
  version: "0.2.0",
  enabled: true,
  category: "content",
  order: 12,
  icon: "🎙️",
  // eslint-disable-next-line no-restricted-syntax -- module tile accent; matches the other moduleConfigs, which all carry a literal here
  color: "#7C3AED",

  routes: [
    { path: "/state-of-play", component: SopAdminList, permissionLevel: "view", useLayout: true },
    { path: "/state-of-play/settings", component: SopSettingsPage, permissionLevel: "edit", useLayout: true, layoutOptions: { maxW: "container.lg", py: 6 } },
    { path: "/state-of-play/topics", component: SopTopics, permissionLevel: "edit", useLayout: true, layoutOptions: { maxW: "container.lg", py: 6 } },
    { path: "/state-of-play/comments", component: SopComments, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "container.lg", py: 6 } },
    { path: "/state-of-play/agenda", component: SopAgenda, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "container.lg", py: 6 } },
    { path: "/state-of-play/new", component: SopWizard, permissionLevel: "create", useLayout: true, layoutOptions: { maxW: "container.xl", py: 4 } },
    { path: "/state-of-play/blank", component: SopEditor, permissionLevel: "create", useLayout: true, layoutOptions: { maxW: "container.xl", py: 4 } },
    // `/present` must precede `/:id` so the literal segment is not eaten by the param.
    { path: "/state-of-play/:id/present", component: SopPresenter, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "container.2xl", py: 4 } },
    { path: "/state-of-play/:id", component: SopEditor, permissionLevel: "edit", useLayout: true, layoutOptions: { maxW: "container.xl", py: 4 } },

    // Public reader routes — no login (seamless public-page pattern)
    { path: "/play", component: SopCatalogue, permissions: [], public: true },
    { path: "/play/topic/:slug", component: SopTopicPage, permissions: [], public: true },
    { path: "/play/:slug", component: SopEpisodePage, permissions: [], public: true },
  ],

  navigation: [
    { label: "Episodes", path: "/state-of-play", icon: "🎙️", permissionLevel: "view" },
    { label: "New Episode", path: "/state-of-play/new", icon: "✨", permissionLevel: "create" },
    { label: "Replies", path: "/state-of-play/comments", icon: "💬", permissionLevel: "view" },
    { label: "Coming up", path: "/state-of-play/agenda", icon: "📋", permissionLevel: "view" },
    { label: "Topics", path: "/state-of-play/topics", icon: "📂", permissionLevel: "edit" },
    { label: "Settings", path: "/state-of-play/settings", icon: "⚙️", permissionLevel: "edit" },
    { label: "Public Catalogue", path: "/play", icon: "🌐", permissions: [] },
  ],

  // Resolved from the backend SSOT at runtime — ships EMPTY on purpose.
  permissions: { view: [], create: [], edit: [], delete: [] },

  quickActions: [
    { label: "New Episode", path: "/state-of-play/new", icon: "🎙️", description: "Talk or type — it makes the diagram and the takeaways" },
    { label: "Listen", path: "/play", icon: "🌐", description: "The public catalogue" },
  ],

  changelog: [
    {
      version: "0.2.0",
      releaseDate: "2026-10-15",
      title: "People, suggested times, series — and POLISH v1",
      features: [
        "✅ People tab: invite from your client list on a phone — search, topic chips, one button; each person's result shown",
        "✅ Suggested times from your calendars — your standing slot first, clashes checked",
        "✅ Coming up: every episode on the calendar, soonest first",
        "✅ Series on the public site — shelves, Start at episode 1, Episode n of N, previous/next, Live on …",
        "✅ Diagrams: lifted nodes, brand-coloured start and outcome, Open full size",
        "✅ POLISH v1 — 14px / 44px floor, words on every button, safe tooltips, light + dark",
      ],
    },
    {
      version: "0.1.0",
      releaseDate: "2026-08-27",
      title: "Initial release — schedule, present, publish",
      features: [
        "✅ 🎙️ State of Play event kind on the calendar — creates the audio room and the episode together",
        "✅ The diagram is authored BEFORE the event; the presenter view puts it on a second screen with your notes and a talking clock",
        "✅ Meet recording pulled in on demand — audio CID, transcript, duration",
        "✅ AI drafts the page from the transcript (qwen3.6-35b via local-ai) — accepted field by field, never auto-published",
        "✅ Public /play catalogue + /play/:slug replay page: player, diagram, three takeaways, digest, one CTA",
        "✅ Tenant default call to action with a per-episode override, and click tracking",
        "✅ Three page skins stored per episode; session-deduped view counting",
        "✅ Topics = series, with per-topic episode numbering; deleting a topic that still holds episodes is refused with a count",
        "✅ Comments on the public page — no login, text or voice note, held for approval; the count tracks what is visible",
        "✅ Raise a reply to the agenda: listeners' replies become the next episode's content",
        "✅ The wizard: ramble by voice or text → in-house transcription → diagram, three takeaways, notes and an ask → pick a time and the calendar event + audio room are created and linked",
      ],
      notes:
        "Built from what episode 0 actually taught us: Meet's summariser is the wrong shape AND was failing on real transcripts, so this module has its own copy service with a real token budget and honest error reporting.",
    },
  ],
};

export default stateOfPlayModuleConfig;
